// src/pages/TsVenueContent.js

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CopyOutlined } from '@ant-design/icons';
import { Tree, Tabs, message, Button, Spin, Modal } from 'antd';
import {
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaFolderOpen,
  FaGripVertical,
  FaPlus,
  FaRegFileAlt,
  FaTrashAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AppContext from '../contexts/AppContext';
import { getTsVenueDisplayName, YARRAWONGA_VENUE_ID } from '../utils/tsVenueDisplayName';

const TS_NEARBY_CONFIGS = {
  [YARRAWONGA_VENUE_ID]: {
    nearbyContentId: 'ts_area_03_content_c2',
    legacyNearbyContentId: 'ts_yarrawonga_visitor_centre_c8',
    label: 'Nearby',
  },
};

function getNearbyConfig(selectedRoleId) {
  return TS_NEARBY_CONFIGS[selectedRoleId] || null;
}

function filterLegacyNearbyNodes(items, selectedRoleId) {
  const config = getNearbyConfig(selectedRoleId);
  if (!config || !Array.isArray(items) || !config.legacyNearbyContentId) {
    return items;
  }

  return items.filter((item) => item?.id !== config.legacyNearbyContentId);
}

function getDisplayNodeName(item, selectedRoleId) {
  if (item?.sectionType === 'nearby' && item.id === item.rootNodeId) return 'Nearby';
  const searchTags = String(item?.searchTags || '');
  if (/_c1$/i.test(searchTags)) {
    return 'Services & Facilities';
  }
  if (/_c2$/i.test(searchTags)) {
    return 'Nearby';
  }
  return getTsVenueDisplayName(item?.name, selectedRoleId);
}

const getNodeIcon = (node) =>
  node?.isLeaf ? (
    <FaRegFileAlt className="h-3.5 w-3.5 text-[#4356f4]" />
  ) : (
    <FaFolderOpen className="h-3.5 w-3.5 text-[#4356f4]" />
  );

// Matches .cms-content-tree-tsvenue .ant-tree-indent-unit in index.css. AntD's
// tree indent eats into the row's own width per level (not just the switcher
// column), so the Visibility/Actions columns drift left of the header as nodes
// nest deeper - most visible on narrow screens. Pulling the row out by the same
// amount per level keeps every row's columns lined up with the header.
const TS_VENUE_TREE_INDENT_PX = 30;

const buildTreeData = (node, parentId = null, handlers, selectedRoleId = null, depth = 0, sectionName = null, rootNodeId = node?.id) => {
  const { toggleVisibility, handleDelete, handleEdit, handleCopyId } = handlers;
  // The root call (parentId === null) IS the section/tab itself - its raw name
  // ("Events", "Tours", etc.) is threaded down to every descendant so leaf
  // editors can tell which section they belong to without walking the tree
  // back up (TsContent has no parent pointer, only child-id arrays).
  const currentSectionName = parentId === null ? node.name : sectionName;
  const nodeName = getDisplayNodeName(node, selectedRoleId) || 'Unnamed';
  const visibilityLabel = typeof node.visible === 'boolean' ? (node.visible ? 'Visible' : 'Hidden') : '-';
  const isSharedRoot = parentId === null && typeof node?.name === 'string' && node.name.includes('(Shared)');
  const editable = !isSharedRoot;
  const indentOffset = depth * TS_VENUE_TREE_INDENT_PX;

  const renderTitle = () => (
    <div
      className="group grid grid-cols-[minmax(0,1fr)_110px_148px] items-center gap-2 rounded-lg border border-transparent px-2 py-2 text-[13px] text-[#42516b] transition-all duration-300 ease-out hover:-translate-y-[1px] hover:border-[#dfe5f8] hover:bg-[#fcfdff] hover:shadow-[0_10px_20px_rgba(15,23,42,0.045)]"
      style={indentOffset ? { marginLeft: -indentOffset, width: `calc(100% + ${indentOffset}px)` } : undefined}
    >
      <div
        className={`flex min-w-0 items-center gap-2 ${editable ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={() => {
          if (!editable) return;
          handleEdit(node.id, {
            ...node,
            parentId,
            content_parent_id: parentId,
            isNoParent: parentId ? false : true,
            editingScope: 'venue',
            tsVenueId: selectedRoleId,
            sectionName: currentSectionName,
            rootNodeId,
          });
        }}
      >
        <span className="text-[#9faac0]">
          <FaGripVertical className="h-3.5 w-3.5" />
        </span>
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#eef2ff] transition-all duration-300 ease-out group-hover:scale-[1.04] group-hover:bg-[#e7edff] group-hover:shadow-[0_8px_16px_rgba(74,92,255,0.10)]">
          {getNodeIcon(node)}
        </span>
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={`truncate font-semibold text-[#1b2b44] ${
              node.isLeaf ? 'text-[13px]' : 'text-[15px]'
            } transition-colors duration-300 ease-out group-hover:text-[#12203a]`}
          >
            {nodeName}
          </div>
          <span className="shrink-0 rounded-full bg-[#f1f4fb] px-2 py-[2px] text-[11px] font-medium uppercase tracking-[0.04em] text-[#a0abc4] transition-all duration-300 ease-out group-hover:bg-[#eaf0ff] group-hover:text-[#8996b6]">
            {node.isLeaf ? 'Entry' : 'Directory'}
          </span>
        </div>
      </div>

      <div className="flex justify-center">
        <span
          className={`inline-flex items-center rounded-full border px-2 py-[2px] text-[12px] font-medium ${
            parentId === null
              ? 'border-[#d9e1f6] bg-[#f9fbff] text-[#5a6884]'
              : node.visible
                ? 'border-[#d5f3e7] bg-[#f3fffa] text-[#22b67f]'
                : 'border-[#ffe0e5] bg-[#fff6f7] text-[#d95a73]'
          }`}
        >
          {visibilityLabel}
        </span>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="inline-flex h-8.5 w-8.5 items-center justify-center rounded-md border border-[#dfe5f4] bg-white text-[#8f9bb4] transition-all duration-250 ease-out hover:-translate-y-[1px] hover:border-[#ced7ec] hover:shadow-[0_8px_16px_rgba(15,23,42,0.07)] hover:text-[#334154]"
          onClick={(event) => {
            event.stopPropagation();
            handleCopyId(node.id);
          }}
          title="Copy Content ID"
        >
          <CopyOutlined />
        </button>

        {typeof node.visible === 'boolean' && parentId !== null && (
          <button
            type="button"
            className="inline-flex h-8.5 w-8.5 items-center justify-center rounded-md border border-[#dfe5f4] bg-white text-[#8f9bb4] transition-all duration-250 ease-out hover:-translate-y-[1px] hover:border-[#ced7ec] hover:shadow-[0_8px_16px_rgba(15,23,42,0.07)] hover:text-[#334154]"
            onClick={(event) => {
              event.stopPropagation();
              toggleVisibility(node);
            }}
            title={node.visible ? 'Hide' : 'Show'}
          >
            {node.visible ? <FaEye className="h-4 w-4" /> : <FaEyeSlash className="h-4 w-4" />}
          </button>
        )}

        <button
          type="button"
          className="inline-flex h-8.5 w-8.5 items-center justify-center rounded-md border border-[#dbe1ff] bg-[#f5f7ff] text-[#4a5cff] transition-all duration-250 ease-out hover:-translate-y-[1px] hover:border-[#c7d0fa] hover:bg-[#eff2ff] hover:shadow-[0_8px_16px_rgba(74,92,255,0.10)]"
          onClick={(event) => {
            event.stopPropagation();
            handleEdit(node.id, {
              ...node,
              parentId,
              content_parent_id: parentId,
              isNoParent: parentId ? false : true,
              editingScope: 'venue',
              tsVenueId: selectedRoleId,
              sectionName: currentSectionName,
              rootNodeId,
            });
          }}
          title="Edit"
        >
          <FaEdit className="h-4 w-4" />
        </button>

        {parentId !== null && (
          <button
            type="button"
            className="inline-flex h-8.5 w-8.5 items-center justify-center rounded-md border border-[#ffd7df] bg-[#fff4f6] text-[#ff4d6d] transition-all duration-250 ease-out hover:-translate-y-[1px] hover:border-[#ffc1cf] hover:bg-[#ffeef2] hover:shadow-[0_8px_16px_rgba(255,77,109,0.10)]"
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(node.id, parentId);
            }}
            title="Delete"
          >
            <FaTrashAlt className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  return {
    title: renderTitle(),
    key: node.id,
    children: Array.isArray(node.attributes)
      ? node.attributes.map((child) => buildTreeData(child, node.id, handlers, selectedRoleId, depth + 1, currentSectionName, rootNodeId))
      : [],
    dataRef: node,
    parentId,
  };
};

const TsVenueContent = () => {
  const { token, userInfo, selectedRole } = useContext(AppContext);
  const navigate = useNavigate();

  const [rawData, setRawData] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const apiBase = process.env.REACT_APP_API_BASE_URL;

  const fetchRawData = useCallback(async () => {
    if (!token || !userInfo?.user?.email || !selectedRole?.id) return;
    setLoading(true);
    try {
      const resp = await fetch(
        `${apiBase}/ts/${selectedRole.id}/content/all-nodes/list`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!resp.ok) {
        message.error('Failed to fetch content list');
        return;
      }
      const data = await resp.json();
      setRawData(filterLegacyNearbyNodes(data, selectedRole?.id));
    } catch (err) {
      console.error(err);
      message.error('Network error while fetching content');
    } finally {
      setLoading(false);
    }
  }, [apiBase, selectedRole?.id, token, userInfo?.user?.email]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!rawData.length || !activeTab) {
      setTreeData([]);
      setExpandedKeys([]);
      return;
    }
    const node = rawData.find((item) => item.id === activeTab);
    if (!node) return;

    const handlers = { toggleVisibility, handleDelete, handleEdit, handleCopyId };
    const root = buildTreeData(node, null, handlers, selectedRole?.id);

    setTreeData([root]);

    const keys = [];
    const traverse = (entry) => {
      keys.push(entry.key);
      (entry.children || []).forEach(traverse);
    };
    traverse(root);
    setExpandedKeys(keys);
  }, [rawData, activeTab, selectedRole?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    fetchRawData();
  }, [fetchRawData]);

  useEffect(() => {
    if (!rawData.length) return;
    const exists = rawData.some((item) => item.id === activeTab);
    if (!activeTab || !exists) {
      setActiveTab(rawData[0].id);
    }
  }, [rawData, activeTab]);

  const handleDelete = (key, parentId) => {
    if (!token || !userInfo?.user?.email) return message.error('Missing auth');
    Modal.confirm({
      title: 'Delete Node',
      content: 'Are you sure to delete?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const resp = await fetch(
            `${apiBase}/ts/content/delete`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                email: userInfo.user.email,
                content_id: key,
                content_parent_id: parentId,
                ts_venue_id: selectedRole.id,
                editing_scope: 'venue',
              }),
            }
          );
          const result = await resp.json();
          if (!resp.ok) {
            return message.error(result.error || 'Delete failed');
          }
          message.success('Deleted');
          fetchRawData();
        } catch {
          message.error('Delete error');
        }
      },
    });
  };

  const handleAdd = async (parentKey) => {
    if (!token || !userInfo?.user?.email) return message.error('Missing auth');
    try {
      const resp = await fetch(
        `${apiBase}/ts/content/add`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: userInfo.user.email,
            content_parent_id: parentKey,
            ts_venue_id: selectedRole.id,
            editing_scope: 'venue',
            isLeaf: true,
            layoutStyle: 'none',
            visible: true,
            name: 'New ',
            description: '',
            imageUrls: [],
            mapUrl: '',
            leftDescription: '',
            rightDescription: '',
            searchTags: '',
            shuffleChildren: false,
            pinToTop: false,
            bannerImage: '',
          }),
        }
      );
      const result = await resp.json();
      if (!resp.ok) {
        return message.error(result.error || 'Add failed');
      }
      message.success('Added');
      fetchRawData();
    } catch {
      message.error('Add error');
    }
  };

  const handleEdit = (key, nodeData) => {
    navigate('/ts/detail/' + key, { state: { content: nodeData } });
  };

  const handleCopyId = async (contentId) => {
    try {
      await navigator.clipboard.writeText(contentId);
      message.success('Content ID copied');
    } catch (error) {
      console.error(error);
      message.error('Failed to copy Content ID');
    }
  };

  const toggleVisibility = async (nodeData) => {
    if (!token || !userInfo?.user?.email) return message.error('Missing auth');
    try {
      const resp = await fetch(
        `${apiBase}/ts/content/visibility`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: userInfo.user.email,
            content_id: nodeData.id,
            visible: !nodeData.visible,
            ts_venue_id: selectedRole.id,
            editing_scope: 'venue',
          }),
        }
      );
      const result = await resp.json();
      if (!resp.ok) {
        return message.error(result.error || 'Visibility update error');
      }
      message.success('Visibility updated');
      fetchRawData();
    } catch {
      message.error('Visibility update error');
    }
  };

  const onDrop = async (info) => {
    if (!token || !userInfo?.user?.email) return message.error('Missing auth');
    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const parts = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(parts[parts.length - 1]);
    const original = (() => {
      const find = (arr, key, parent = null) => {
        for (let i = 0; i < arr.length; i += 1) {
          const item = arr[i];
          if (item.key === key) return { parentId: parent, node: item };
          if (item.children) {
            const found = find(item.children, key, item.key);
            if (found) return found;
          }
        }
        return null;
      };
      return find(treeData, dragKey);
    })();
    const originalParentId = original?.parentId;
    try {
      const resp = await fetch(
        `${apiBase}/ts/content/reorder`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            dragKey,
            originalParentId,
            dropKey,
            targetParentId: info.dropToGap ? (originalParentId || '') : dropKey,
            dropPosition,
            ts_venue_id: selectedRole.id,
            editing_scope: 'venue',
          }),
        }
      );
      const result = await resp.json();
      if (!resp.ok) {
        message.error(result.error || 'Reorder failed');
      } else {
        message.success('Reordered');
      }
    } catch {
      message.error('Reorder error');
    }
    fetchRawData();
  };

  const onExpand = (nextExpandedKeys) => {
    setExpandedKeys(nextExpandedKeys);
  };

  const currentRootNode = treeData[0];
  const childCount = currentRootNode?.children?.length || 0;
  const tabItems = useMemo(
    () => rawData.map((item) => ({
      key: item.id,
      label: getDisplayNodeName(item, selectedRole?.id),
    })),
    [rawData, selectedRole?.id]
  );

  return (
    <div className="mx-auto mb-20 w-[92%] max-w-[1380px] py-2">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="max-w-2xl text-[13px] leading-7 text-[#5f6f89]">
            Organise and manage the sections and pages in your touchscreen content while keeping the
            existing drag, visibility, edit and delete workflows intact.
          </p>
        </div>

        <Button
          type="primary"
          icon={<FaPlus />}
          className="!mt-1 !h-10 !rounded-xl !border-0 !bg-[#4a5cff] !px-5 !font-semibold !shadow-[0_14px_28px_rgba(74,92,255,0.20)] transition-all duration-200 hover:!-translate-y-0.5 hover:!bg-[#3f50e6] hover:!shadow-[0_18px_34px_rgba(74,92,255,0.26)]"
          onClick={() => activeTab && handleAdd(activeTab)}
          disabled={!activeTab}
        >
          Add Entry
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className="mb-4"
      />

      <div className="rounded-[28px] border border-[#e2e7f6] bg-white p-3 shadow-[0_16px_36px_rgba(15,23,42,0.05)]">
        {/* Header and rows are squeezed differently by the browser once the
            column-1 name/content area gets too narrow, which throws off the
            Visibility/Actions column alignment on small screens no matter how
            precisely the row indentation is compensated for. Giving both a
            shared minimum width and letting this section scroll horizontally
            instead keeps them rendered at the same width always, so they
            line up by construction rather than by calculation. */}
        <div className="overflow-x-auto">
          <div className="min-w-[480px]">
            <div className="mb-2 grid grid-cols-[minmax(0,1fr)_110px_148px] gap-2 rounded-lg bg-[#f8faff] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#98a4bd]">
              <div className="min-w-0 truncate">Content Item</div>
              <div className="text-center">Visibility</div>
              <div className="text-right">Actions</div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Spin size="large" />
              </div>
            ) : (
              <div className="rounded-lg border border-[#e9edf7] bg-white px-1 py-0.5">
                <Tree
                  className="cms-content-tree cms-content-tree-tsvenue"
                  draggable
                  blockNode
                  treeData={treeData}
                  expandedKeys={expandedKeys}
                  onExpand={onExpand}
                  onDrop={onDrop}
                  allowDrop={({ dropPosition }) => dropPosition !== -1}
                  showLine={{ showLeafIcon: false }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 border-t border-[#edf1f8] pt-4 text-[12px] text-[#6f7c98] md:flex-row md:items-center md:justify-between">
          <div>
            Showing the current tab with <span className="font-semibold text-[#2f3c56]">{childCount}</span>{' '}
            direct child items.
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#96a2bc]">
            <span className="rounded-full border border-[#e7ebf7] bg-[#fafbff] px-3 py-1">Drag to reorder</span>
            <span className="rounded-full border border-[#e7ebf7] bg-[#fafbff] px-3 py-1">Click row to edit</span>
            <span className="rounded-full border border-[#e7ebf7] bg-[#fafbff] px-3 py-1">Use tabs to switch sections</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TsVenueContent;




